import { z } from "zod"

/**
 * Canonical zod schema for a single programmatic-SEO landing page.
 *
 * This is the ONE render contract shared between the AIX content control plane,
 * product renderers, release compiler, and delivery Worker.
 */
export const seoRightsStatusSchema = z.enum(["owned", "licensed", "public-domain", "reference-only", "unknown"])

export const seoSourceTypeSchema = z.enum(["first-party", "generated", "licensed", "scraped", "manual"])

export const seoMediaRoleSchema = z.enum(["hero", "inline", "example", "comparison", "social-share", "before", "after"])

export const seoEvidenceTaxonomySchema = z.object({
  modality: z.enum(["image", "video", "text-to-image", "text-to-video", "image-to-video"]).optional(),
  aspectRatio: z
    .string()
    .regex(/^\d+:\d+$/)
    .optional(),
  durationSeconds: z.number().positive().optional(),
  styles: z.array(z.string()).optional(),
  subject: z.string().optional(),
  industry: z.string().optional(),
  videoType: z.enum(["paid-social-ad", "educational", "product-demo", "ugc-ad", "explainer", "tutorial"]).optional(),
})

export const seoMediaProvenanceSchema = z.object({
  sourceType: seoSourceTypeSchema,
  sourceId: z.string().optional(),
  sourceUrl: z.string().url().optional(),
  model: z.string().optional(),
  modelVersion: z.string().optional(),
  prompt: z.string().optional(),
  settings: z.record(z.string(), z.unknown()).optional(),
  generatedAt: z.string().datetime().optional(),
  generationRunId: z.string().optional(),
  costMicros: z.number().int().nonnegative().optional(),
})

export const seoMediaRightsSchema = z.object({
  status: seoRightsStatusSchema,
  license: z.string().optional(),
  sourceUrl: z.string().url().optional(),
  reviewedAt: z.string().datetime().optional(),
})

const seoMediaFields = {
  assetId: z.string().optional(),
  role: seoMediaRoleSchema.optional(),
  src: z.string(),
  alt: z.string(),
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
  caption: z.string().optional(),
  credit: z.string().optional(),
  rights: seoMediaRightsSchema.optional(),
  provenance: seoMediaProvenanceSchema.optional(),
}

export const seoHeroImageSchema = z.object({
  ...seoMediaFields,
  type: z.literal("image").optional(),
})

export const seoMediaAssetSchema = z.discriminatedUnion("type", [
  z.object({
    ...seoMediaFields,
    type: z.literal("image"),
  }),
  z.object({
    ...seoMediaFields,
    type: z.literal("video"),
    durationSeconds: z.number().positive().optional(),
    poster: z.string().optional(),
    captionTrackSrc: z.string().optional(),
  }),
])

export const seoBeforeAfterMediaSchema = z.object({
  before: seoMediaAssetSchema,
  after: seoMediaAssetSchema,
  caption: z.string().optional(),
})

export const seoEvidenceRecordSchema = z.object({
  id: z.string(),
  title: z.string().optional(),
  sourceType: seoSourceTypeSchema,
  sourceUrl: z.string().url().optional(),
  capturedAt: z.string().datetime(),
  rightsStatus: seoRightsStatusSchema,
  contentFingerprint: z.string(),
  supportingClaims: z.array(z.string()).optional(),
  prompt: z.string().optional(),
  model: z.string().optional(),
  modelVersion: z.string().optional(),
  generationRunId: z.string().optional(),
  costMicros: z.number().int().nonnegative().optional(),
  outputAssetIds: z.array(z.string()).optional(),
  taxonomy: seoEvidenceTaxonomySchema.optional(),
})

export const seoGenerationRunSchema = z.object({
  id: z.string(),
  kind: z.enum(["research", "source", "generate", "media", "check", "fix", "stage", "publish", "observe"]),
  status: z.enum(["planned", "running", "succeeded", "failed", "partial"]),
  idempotencyKey: z.string(),
  startedAt: z.string().datetime(),
  completedAt: z.string().datetime().optional(),
  parentRunId: z.string().optional(),
  inputHash: z.string(),
  outputHashes: z.array(z.string()).default([]),
  model: z.string().optional(),
  modelVersion: z.string().optional(),
  costMicros: z.number().int().nonnegative().default(0),
  itemBudget: z.number().int().positive().optional(),
  costBudgetMicros: z.number().int().positive().optional(),
  error: z.string().optional(),
})

export const seoValidationFindingSchema = z.object({
  ruleId: z.string(),
  severity: z.enum(["error", "warning"]),
  message: z.string(),
  field: z.string().optional(),
  autoFixable: z.boolean(),
  owner: z.string().optional(),
  repairStatus: z.enum(["open", "in-progress", "fixed", "wont-fix"]).optional(),
  createdAt: z.string().datetime().optional(),
})

export const seoApprovalRecordSchema = z.object({
  id: z.string(),
  scope: z.enum(["keyword", "content", "media", "claims", "publish"]),
  subjectId: z.string(),
  status: z.enum(["pending", "approved", "rejected"]),
  reviewer: z.string().optional(),
  reviewedAt: z.string().datetime().optional(),
  note: z.string().optional(),
  evidenceIds: z.array(z.string()).default([]),
})

export const seoIndexationObservationSchema = z.object({
  slug: z.string().startsWith("/"),
  observedAt: z.string().datetime(),
  source: z.enum(["gsc", "live-probe", "manual"]),
  status: z.enum(["discovered", "crawled", "indexed", "not-indexed", "blocked", "unknown"]),
  detail: z.string().optional(),
})

export const seoBacklinkPlacementSchema = z.object({
  id: z.string(),
  destinationUrl: z.string().url(),
  targetSlug: z.string().startsWith("/"),
  placementType: z.enum(["directory", "listicle", "review", "creator", "guest", "partner", "press"]),
  status: z.enum(["prospect", "pitched", "accepted", "live", "rejected", "removed"]),
  anchor: z.string().optional(),
  costMicros: z.number().int().nonnegative().default(0),
  approvedAt: z.string().datetime().optional(),
  discoveredAt: z.string().datetime().optional(),
  referralUtm: z.string().optional(),
})

export const seoBeforeAfterSchema = z.object({
  before: z.string(),
  after: z.string(),
})

export const seoExampleSchema = z.object({
  title: z.string(),
  prompt: z.string().optional(),
  result: z.string(),
  media: z.array(seoMediaAssetSchema).optional(),
})

export const seoFaqItemSchema = z.object({
  q: z.string(),
  a: z.string(),
})

export const seoComparisonTableSchema = z.object({
  caption: z.string().optional(),
  columns: z.array(z.string()),
  rows: z.array(z.array(z.string())),
})

export const seoSectionSchema = z.object({
  heading: z.string(),
  body: z.array(z.string()),
  code: z
    .array(
      z.object({
        label: z.string().optional(),
        content: z.string(),
      }),
    )
    .optional(),
  image: seoHeroImageSchema.optional(),
})

/** Template variant selected by the product renderer registry. */
export const seoTemplateKeySchema = z.enum(["landing", "comparison", "guide", "gallery", "creative-education"])

export const seoContentKindSchema = z.enum([
  "tool",
  "prompt-library",
  "template",
  "comparison",
  "guide",
  "industry",
  "model",
  "creative-education",
  "creative-teardown",
  "video-type-guide",
])

export const seoCtaSchema = z.object({
  label: z.string(),
  href: z.string(),
  supportingText: z.string().optional(),
})

export const seoFactualFreshnessSchema = z.object({
  subject: z.string(),
  observedAt: z.string().datetime(),
  pricingObservedAt: z.string().datetime().optional(),
  freshUntil: z.string().datetime(),
})

export const seoReviewerSchema = z.object({
  name: z.string(),
  role: z.string().optional(),
  url: z.string().url().optional(),
})

export const seoAudienceFitSchema = z.object({
  bestFor: z.array(z.string()),
  notFor: z.array(z.string()),
})

/** Product-neutral programmatic SEO landing page. */
export const seoPageSchema = z.object({
  /** URL path, e.g. "/ai-humanizer" */
  slug: z.string(),
  /** Optional template variant; consumers default to "landing" when absent */
  templateKey: seoTemplateKeySchema.optional(),
  /** Version of the template contract used to create this revision. */
  templateVersion: z.string().optional(),
  /** Semantic page family used by product-specific renderers and quality gates. */
  contentKind: seoContentKindSchema.optional(),
  metaTitle: z.string(),
  metaDescription: z.string(),
  h1: z.string(),
  /** One-sentence benefit under the H1 */
  promise: z.string(),
  keyword: z.string(),
  /** Product-specific use-case bullets. */
  useCases: z.array(z.string()).optional(),
  /** Optional transformation example for rewriting products. */
  beforeAfter: seoBeforeAfterSchema.optional(),
  /** Optional prompt/result examples for generative products. */
  examples: z.array(seoExampleSchema).optional(),
  faq: z.array(seoFaqItemSchema),
  /** Sibling and hub slugs for internal linking. */
  internalLinks: z.array(z.string()),
  /** Primary product action. */
  cta: seoCtaSchema.optional(),
  /** Optional long-form body paragraphs (rendered after the tool) */
  body: z.array(z.string()).optional(),
  /** Optional hero image (R2 URL) shown under the headline */
  heroImage: seoHeroImageSchema.optional(),
  /** Typed first-party or licensed media rendered throughout the page. */
  media: z.array(seoMediaAssetSchema).optional(),
  /** Paired media showing a controlled before/after transformation. */
  beforeAfterMedia: seoBeforeAfterMediaSchema.optional(),
  /** Source evidence supporting examples and factual claims. */
  evidence: z.array(seoEvidenceRecordSchema).optional(),
  /** Dated factual scope for comparison or other freshness-sensitive content. */
  factualFreshness: seoFactualFreshnessSchema.optional(),
  /** Optional comparison table for "best X" / "X vs Y" pages */
  comparisonTable: seoComparisonTableSchema.optional(),
  /** Optional structured article sections (heading + paragraphs + optional inline image/code). */
  sections: z.array(seoSectionSchema).optional(),
  /** First publication time, stamped by the release compiler from the page record; never authored. */
  publishedAt: z.string().datetime().optional(),
  /** Last substantive editorial update (visible date + `dateModified`). Not the DB row timestamp, which moves on state changes. The release compiler fills it from the latest content revision only when not authored. */
  updatedAt: z.string().datetime().optional(),
  /** Answer-first passage under the H1 that fully answers the query on its own (≤150 words). */
  answer: z.string().optional(),
  /** Short standalone bullets summarizing the page. */
  keyTakeaways: z.array(z.string()).optional(),
  /** Who the recommendation suits and who should look elsewhere. */
  audienceFit: seoAudienceFitSchema.optional(),
  /** Cost, limits, risks, or common mistakes a reader should know before choosing. */
  limitations: z.array(z.string()).optional(),
  /** How the comparison or recommendation was produced. */
  methodology: z.string().optional(),
  /** Named person who reviewed the page. */
  reviewer: seoReviewerSchema.optional(),
})

/** A product's full SEO surface: the hub index + its leaf pages. */
export const productSeoContentSchema = z.object({
  hub: z.object({
    slug: z.string(),
    title: z.string(),
    metaTitle: z.string(),
    metaDescription: z.string(),
    intro: z.string(),
  }),
  pages: z.array(seoPageSchema),
})

export type SeoTemplateKey = z.infer<typeof seoTemplateKeySchema>
export type SeoPage = z.infer<typeof seoPageSchema>
export type ProductSeoContent = z.infer<typeof productSeoContentSchema>
export type SeoPageComparisonTable = z.infer<typeof seoComparisonTableSchema>
export type SeoPageSection = z.infer<typeof seoSectionSchema>
export type SeoPageExample = z.infer<typeof seoExampleSchema>
export type SeoMediaAsset = z.infer<typeof seoMediaAssetSchema>
export type SeoEvidenceRecord = z.infer<typeof seoEvidenceRecordSchema>
export type SeoFactualFreshness = z.infer<typeof seoFactualFreshnessSchema>
export type SeoMediaRole = z.infer<typeof seoMediaRoleSchema>
export type SeoEvidenceTaxonomy = z.infer<typeof seoEvidenceTaxonomySchema>
export type SeoGenerationRun = z.infer<typeof seoGenerationRunSchema>
export type SeoValidationFinding = z.infer<typeof seoValidationFindingSchema>
export type SeoApprovalRecord = z.infer<typeof seoApprovalRecordSchema>
export type SeoIndexationObservation = z.infer<typeof seoIndexationObservationSchema>
export type SeoBacklinkPlacement = z.infer<typeof seoBacklinkPlacementSchema>
export type SeoReviewer = z.infer<typeof seoReviewerSchema>
export type SeoAudienceFit = z.infer<typeof seoAudienceFitSchema>
